import React, { useEffect, useState, useRef, ReactNode, ChangeEvent } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import Form from 'react-bootstrap/Form';
import { useForm, useFieldArray } from "react-hook-form";
import toast from 'react-hot-toast';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';

import "react-datepicker/dist/react-datepicker.css";
import { GustoPayrollHournalFormsI } from '../../resources/form-props';
import { gustoApi } from '../../resources/hooks/api/gustoApiHook';
import { GustoPayrollJournalValidate } from '../../resources/form-validator';
import { transTableDataApi } from '../../resources/hooks/api/transApiHook';
import { getBranch } from '../../store/user/selectors'
import { getGustoImportedStep, getGustoStepsCompleted } from '../../store/settings/selectors'
// import { getThirdPartyStepsCompleted, getThirdPartyImportedStep } from '../../store/settings/selectors'
import DatePickerModal from '../../components/Modal/DatePickerModal/DatePickerModal';
import GustoJournalDetailsModal from '../../components/Modal/GustoJournalDetails/GustoJournalDetailsModal';

import { GL_VP, MONTHS, SELECT_CUSTOM_STYLE, URLS } from '../../resources/constants';
import { lastYears, calculateMaxHeightContent, usdCurrencyFormat } from '../../resources/functions';
import Pagination from '@mui/material/Pagination';
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook';
import { currencyFormat } from '../../resources/helper';

interface Props {

}

const PayrollJournalList: React.FC<Props> = () => {

    const { gustoFetchPayrollJournal, gustoFetchPayrollDetails, gustoImportData } = gustoApi();
    const { createTransactionAdjustment } = transTableDataApi()
    const { fetchAccountSettingsData } = useSettingsApi();

    const gustoStepCompleted = useSelector(getGustoStepsCompleted);
    const gustoImportedStep = useSelector(getGustoImportedStep);

    const navigate = useNavigate();
    const branchData = useSelector(getBranch)

    const [records, setRecords] = useState<any>({
        data: [],
        total_records: 0,
    });
    const [searchParams, setSearchParams] = useState<any>({
        page: 1,
        per_page: 6
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const [isOpenDateModal, setIsOpenDateModal] = useState<boolean>(false);
    const [isOpenJournalDetailsModal, setIsOpenJournalDetailsModal] = useState<boolean>(false);
    const [selectedJournal, setSelectedJournal] = useState<any>(null);
    const checkAllRef = useRef<HTMLInputElement>(null);
    const [selectedDate, setSelectedDate] = useState<Date[]>([]);

    const { register, control, trigger, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm<GustoPayrollHournalFormsI>({
        defaultValues: {
            payrolls: []
        },
        resolver: yupResolver(GustoPayrollJournalValidate)
    });
    const { fields, append: formAppend, remove: formRemove } = useFieldArray({
        control,
        name: "payrolls"
    });

    useEffect(() => {
        records.data.map((record: any, index: number) => {
            formAppend({
                formIndex: index,
                checked: true,
                record_id: record['id'],
                period_start_date: record['pay_period_start_date'] || null,
                period_end_date: record['pay_period_end_date'] || null,
                gross_pay: record['breakdown']['gross_pay'],
                net_pay: record['breakdown']['net_pay'],
                import_type: record['import_type'],
                status: record['status'],
            })
        })
    }, [records.data])

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    })

    useEffect(() => {
        if (fields.length == 0) {
            if (checkAllRef.current) {
                checkAllRef.current.checked = false;
            }
        }
        if (fields.length == records.data.length) {
            if (checkAllRef.current) {
                checkAllRef.current.checked = true;
            }
        }
    }, [fields])

    useEffect(() => {
        fetchTransactionList()
    }, [])


    const handleCheckAllChange = (e: any) => {
        if (checkAllRef.current) {
            checkAllRef.current.checked = e.target.checked;

            records.data.map((record: any, index: number) => {
                if (!(record['duplicate'] || record['posted'])) {
                    setValue(`payrolls.${index}.checked`, e.target.checked)
                }
            })
        }
    };

    const fetchTransactionList = () => {
        const params: any = {
            branch_id: +branchData['id'],
            "page": searchParams['page'],
            "per_page": searchParams['per_page'],
        }

        reset({
            payrolls: []
        })

        setIsLoading(true);
        gustoFetchPayrollJournal(params, (message: string, resp: any) => {
            setRecords({
                data: resp.data.data.result,
                total_records: resp.data.data.total,
            });
            setIsLoading(false);

            // if (resp.data.data.total == 0) {
            //     getSettingsData();
            // }

        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const handleCheckAll = (e: any, index: number) => {
        // setValue(`payrolls.${index}.checked`, e.target.checked);

        let recordPos = index;
        if (e.target.checked) {
            do {
                setValue(`payrolls.${recordPos}.checked`, true);
                recordPos = recordPos - 1;
            } while (recordPos >= 0);
        } else {
            while (recordPos <= records.data.length - 1) {
                setValue(`payrolls.${recordPos}.checked`, false);
                recordPos = recordPos + 1;
            }
        }


        if (e.target.checked) {
            const checkedRecords = records.data.filter((record: any, index: number) => getValues(`payrolls.${index}.checked`) === true);

            if (checkedRecords.length == records.data.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = true;
                }
            }
        } else {
            const uncheckedRecords = records.data.filter((record: any, index: number) => getValues(`payrolls.${index}.checked`) === false);

            if (uncheckedRecords.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = false;
                }
            }
        }
    }

    const processData = (data: any) => {
        const selectedRecords = data['payrolls']
            .map((d: any, index: number) => {
                if (d['checked']) {
                    return {
                        // id: d.id,
                        month: d.month,
                        year: d.year
                    };
                } else {
                    return null;
                }
            })
            .filter((e: any) => e); // Filter out null values
        if (selectedRecords.length > 0) {
            postData(selectedRecords);
        } else {
            toast.error('Please select at least one record to submit');
            return;
        }

    };
    const postData = (records: any) => {
        /*setIsLoading(true);
        qbPostBSPL({ records, branch_id: +branchData['id'] }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.success(message)
            fetchTransactionList();
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })*/
    }

    const onChangePage = (event: any, page: number) => {
        setSearchParams((prevState: any) => ({
            ...prevState,
            page: page
        }))
    }

    const getSettingsData = () => {
        setIsLoading(true)
        fetchAccountSettingsData({ branch_id: +branchData['id'] }, (msg: string, resp: any) => {
            setIsLoading(false)
            toast.success('No data left to process');
            navigate(URLS.DASHBOARD);
        }, (msg: string, resp: any) => {
            setIsLoading(false)
        })
    }

    const _handleGustoImportData = (step: number) => {
        if (!selectedDate.length) {
            setIsOpenDateModal(true);
            return;
        }

        const params: any = {};
        params['branch_id'] = +branchData['id'];
        params['step'] = step;

        params['month'] = +moment(selectedDate[0]).startOf('month').format('MM');
        params['year'] = +moment(selectedDate[0]).startOf('month').format('YYYY');

        setIsLoading(true)
        gustoImportData(params, (message: string, resp: any) => {
            setIsLoading(false);
            setSelectedDate([])
            toast.success(message);
            fetchTransactionList();
        }, (message: string, resp: any) => {
            setIsLoading(false)
            setSelectedDate([])
            toast.error(message)
        })
    }

    useEffect(() => {
        fetchTransactionList()
    }, [searchParams['page']])

    const _handleViewJournalDetails = (id: number) => {
        const params: any = {
            branch_id: +branchData['id'],
            id: id
        }

        setIsLoading(true);
        gustoFetchPayrollDetails(params, (message: string, resp: any) => {
            setIsLoading(false);
            setSelectedJournal(resp.data.data);
            setIsOpenJournalDetailsModal(true);
            // Process the response data if needed
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const _handleCloseDetailsModal = () => {
        setIsOpenJournalDetailsModal(false);
        setSelectedJournal(null);

        fetchTransactionList();
    }

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-4 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h3 font-weight-700 mb-0 d-inline-flex">
                                Payroll Journal Import
                            </h1>
                        </div>
                    </div>
                </div>

                <div className=" executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Form>
                            <div className='flex justify-content-end text-end'>
                                <button type="button" className="btn btn-cancel" onClick={() => setIsOpenDateModal(true)}>Fetch From Gusto</button>
                                &nbsp;
                                {/* {
                                    (fields.length > 0) && (
                                        <button type="button" className="btn btn-primary" onClick={handleSubmit(processData)}>Verify And Submit</button>
                                    ) || <></>
                                } */}
                            </div>


                            <br></br>
                            {/* <div className="table-responsive list-table table-scroll" style={{ maxHeight: contentMaxHeight }}> */}
                            <div className="table-responsive list-table table-scroll">
                                <table className="table table-default">
                                    <thead>
                                        <tr className="align-middle">
                                            {/* <th className="w-5 text-start">
                                                <div className="form-check ">
                                                    <label >
                                                        <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" onChange={handleCheckAllChange} />
                                                    </label>
                                                </div>
                                            </th> */}
                                            <th className="text-center w-5">Pay Period</th>
                                            <th className="text-center w-5">Gross Pay</th>
                                            <th className="text-center w-5">Net Pay </th>
                                            <th className="text-center w-5">Import Type </th>
                                            <th className="text-center w-5">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            fields.length > 0 ?
                                                fields.map((data: any, index: any) => (
                                                    <tr className="align-middle" key={data['id']}>
                                                        {/* <td className="text-start">
                                                            <div className="form-check ">
                                                                <label >
                                                                    <input className="form-check-input wh-20 checkbox" {...register(`payrolls.${index}.checked`)}
                                                                        name={`payrolls.${index}.checked`}
                                                                        defaultChecked={fields[index]['checked']}
                                                                        type="checkbox"
                                                                        onChange={(e: any) => handleCheckAll(e, index)} />
                                                                </label>
                                                            </div>
                                                        </td> */}
                                                        <td className={"text-left"}>
                                                            <strong>

                                                                {fields[index].period_start_date ? moment(fields[index].period_start_date).format('MMMM DD, YYYY') : 'NA'} - {fields[index].period_end_date ? moment(fields[index].period_end_date).format('MMMM DD, YYYY') : 'NA'}
                                                            </strong>
                                                        </td>
                                                        <td className={"text-right"}>
                                                            <strong>{currencyFormat((fields[index].gross_pay || 0).toFixed(2))}</strong>
                                                        </td>
                                                        <td className={"text-right"}>
                                                            <strong>{currencyFormat((fields[index].net_pay || 0).toFixed(2))}</strong>
                                                        </td>
                                                        <td className={"text-center"}>
                                                            <strong>{fields[index].import_type == 1 ? 'Manual' : 'Auto'}</strong>
                                                        </td>
                                                        <td className={"text-center"}>
                                                            {
                                                                fields[index].status == 1 ? 
                                                                (<span className='cursor-pointer' title='Details' onClick={() => 
                                                                    _handleViewJournalDetails(fields[index].record_id)
                                                                }><i className='fa-solid fa-eye'></i></span>) : (
                                                                    <span>
                                                                        <strong>Imported</strong>&nbsp;
                                                                        {
                                                                            fields[index].period_start_date ? <Link to={`${URLS.VIEW_REGISTER}?year=${moment(fields[index].period_start_date).format('YYYY')}&month=${moment(fields[index].period_start_date).format('MM')}`} className='text-primary steps-panel-a p-0'>View Register</Link> : <></>
                                                                        }
                                                                        
                                                                    </span>
                                                                )
                                                            }
                                                            &nbsp;
                                                            {/* <button type='button' className='btn btn-warning btn-sm' onClick={() => navigate('/quickbook-bs/' + fields[index].month + '/' + fields[index].year)}>View BS</button> */}
                                                        </td>
                                                    </tr>
                                                )) : <tr>
                                                    <td colSpan={4} className='text-center'>No data found</td>
                                                </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </Form>
                    </div>
                    <div className='d-flex justify-content-center'>
                        <Pagination count={Math.ceil(records['total_records'] / searchParams['per_page'])} showFirstButton showLastButton onChange={onChangePage} />
                    </div>

                </div>
            </div>

            {
                isOpenDateModal &&
                <DatePickerModal 
                    title={'Import Payroll Journal'} 
                    step={+gustoStepCompleted !== 2 ? +gustoStepCompleted + 1 : +gustoStepCompleted} 
                    selectDate={setSelectedDate} 
                    handleBBDateSelection={() => _handleGustoImportData(+gustoStepCompleted !== 2 ? +gustoStepCompleted + 1 : +gustoStepCompleted)} 
                    closeModal={() => setIsOpenDateModal(false)} 
                />
            }
            {
                isOpenJournalDetailsModal &&
                <GustoJournalDetailsModal title={'Journal Details'} data={selectedJournal} branchData={branchData} closeModal={() => _handleCloseDetailsModal()} />
            }
        </div>

    )
}

export default PayrollJournalList;